import { NavLink } from 'react-router-dom';

/**
 * The two halves of the vehicles-out module. (E13 / T095.)
 *
 * Ricky's ask was that loan cars and test drives live in one module with two
 * functions. They belong together for a physical reason: both are "one of our
 * cars is out with somebody", both draw on the same fleet, and both fit the
 * same trade plates. A workshop foreman and a sales manager are asking the same
 * question in different words.
 *
 * ── But they are not one dataset ──
 * A test drive is sales, a loan car is service, and E2 spent an epic
 * establishing that a sales surface never silently counts service work. So this
 * is deliberately a pair of tabs over two separate screens, each with its own
 * rows, its own filters and its own totals — not a merged table with a type
 * column. Nothing here ever adds the two together. The trade plates section
 * underneath both is the one genuinely shared thing, and it is shared because
 * the plates are physical equipment, not customer records.
 *
 * ── Switching tabs switches the workspace, on purpose ──
 * Test drives sit in the Sales app and loan cars in Service (see lib/apps.js),
 * so moving between them changes the side nav. That is not a glitch to paper
 * over: you have moved from the sales floor to the workshop, and the nav
 * telling you so is the segregation being visible rather than merely true.
 */
const TABS = [
  { to: '/test-drives', label: 'Test drives', hint: 'Sales' },
  { to: '/loan-cars',   label: 'Loan cars',   hint: 'Service' },
];

export default function FleetTabs() {
  return (
    <div
      role="tablist"
      aria-label="Vehicles out"
      style={{
        display: 'flex', gap: 4, alignItems: 'flex-end',
        borderBottom: '1px solid var(--border-color)',
        margin: '-2px 0 2px',
      }}
    >
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          role="tab"
          style={({ isActive }) => ({
            display: 'flex', alignItems: 'baseline', gap: 6,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: isActive ? 600 : 500,
            color: isActive ? 'var(--text-primary)' : 'var(--text-tertiary)',
            borderBottom: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
            marginBottom: -1,
            textDecoration: 'none',
          })}
        >
          {({ isActive }) => (
            <>
              <span>{t.label}</span>
              {/* The half each tab belongs to, said out loud. Someone landing
                  here from a report should not have to infer which side of the
                  business they are looking at. */}
              <span
                className="mono text-xs"
                style={{ color: isActive ? 'var(--text-tertiary)' : 'var(--text-quaternary, var(--text-tertiary))', opacity: isActive ? 1 : 0.7 }}
              >
                {t.hint}
              </span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}
